import {Component, forwardRef, inject, input, OnInit} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {CategoriesGateway} from '@em/shared/api-interface/lib/gateways/categories.gateway';
import {GetCategoriesResp} from '@em/shared/api-interface/lib/types/view-models/get-categories';
import {EmButtonModule} from '@em/shared/ui';
import {CountryCode, ITypedControlValueAccessor} from '@em/shared/util-types';
import {ScopedCountryService} from '@em/user-account/data-access';
import {TranslateModule} from '@ngx-translate/core';
import {TreeNode} from 'primeng/api';
import {TreeSelectModule} from 'primeng/treeselect';
import {filter, switchMap, take} from 'rxjs';
import {TreeNodeSelectEvent, TreeNodeUnSelectEvent} from 'primeng/tree';

@Component({
  selector: 'em-category-selection',
  templateUrl: './category-selection.component.html',
  styleUrls: ['./category-selection.component.scss'],
  imports: [TranslateModule, EmButtonModule, TreeSelectModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EmCategorySelectionComponent),
      multi: true,
    },
  ],
})
export class EmCategorySelectionComponent
  implements ITypedControlValueAccessor<string[]>, OnInit
{
  private readonly _categoriesGateway = inject(CategoriesGateway);
  private readonly _scopedCountry = inject(ScopedCountryService);

  appendTo = input<string>('self');

  categoriesTree: TreeNode[] | undefined;
  selectedNodes: TreeNode[] = [];
  disabled = false;
  countryCode$ = toObservable(this._scopedCountry.getCountryCode());
  private _currentValues: string[] = [];
  private _onChangeCallback?: (val: string[] | null | undefined) => void;
  private _onTouchedCallback?: () => void;

  ngOnInit(): void {
    this._loadCategories();
  }

  writeValue(obj: string[] | null | undefined): void {
    this._currentValues = obj || [];
  }

  registerOnChange(fn: (val: string[] | null | undefined) => void): void {
    this._onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouchedCallback = fn;
  }

  onTouched() {
    if (this._onTouchedCallback) this._onTouchedCallback();
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  isSelected(node: TreeNode): boolean {
    return this.selectedNodes.includes(node);
  }

  private _loadCategories() {
    this.countryCode$
      .pipe(
        filter((code): code is CountryCode => code !== null),
        take(1),
        switchMap((code) =>
          this._categoriesGateway.getCategories({country: code}),
        ),
      )
      .subscribe((resp) => {
        const {available, selected} = this.buildTreeNodes(
          resp,
          this._currentValues,
        );

        this.categoriesTree = available;
        this.selectedNodes = selected;

        this.categoriesTree.forEach((node) => {
          this.calculateSelectedProducts(node);
        });
      });
  }

  private buildTreeNodes(
    categories: GetCategoriesResp,
    selectedCategories: string[] = [],
  ): {available: TreeNode[]; selected: TreeNode[]} {
    const finalTree: TreeNode[] = [];
    const selectedNoes: TreeNode[] = [];

    categories
      .sort((a, b) => a.category.localeCompare(b.category))
      .forEach((category) => {
        const pathParts = category.category.split(' > ');
        let currentLevel = finalTree;

        pathParts.forEach((part, index) => {
          let existingNode = currentLevel.find((node) => node.label === part);
          const key = pathParts.slice(0, index + 1).join(' > ');

          if (!existingNode) {
            existingNode = {
              label: part,
              key,
              data: {
                productCount: 0,
                nestedProductCount: 0,
                selectedProducts: 0,
              },
              children: [],
              expanded: false,
              partialSelected: true,
            };
            currentLevel.push(existingNode);
            if (selectedCategories.includes(key)) {
              selectedNoes.push(existingNode);
            }
          }

          if (category.category === key) {
            existingNode.data.productCount += category.product_count;
          } else {
            existingNode.data.nestedProductCount += category.product_count;
          }

          currentLevel = existingNode.children || [];
        });
      });
    return {
      available: finalTree,
      selected: selectedNoes,
    };
  }

  calculateSelectedProducts(treeNode: TreeNode): number {
    const index = this.selectedNodes.indexOf(treeNode);
    const isSelected = index !== -1;
    const currentSelectedCount = isSelected
      ? treeNode.data?.productCount || 0
      : 0;

    if (treeNode.children?.length) {
      let subCount = 0;
      treeNode.children.forEach((child) => {
        const childCount = this.calculateSelectedProducts(child);
        subCount += childCount;
      });
      treeNode.data.selectedProducts = currentSelectedCount + subCount;
      return currentSelectedCount + subCount;
    } else {
      treeNode.data.selectedProducts = currentSelectedCount;
      return currentSelectedCount;
    }
  }

  getNestedStelectedProducts(node: TreeNode): number {
    if (node.children?.length) {
      let count = 0;
      node.children?.forEach((child) => {
        count += this.getNestedStelectedProducts(child);
      });
      return count;
    }

    return 0;
  }

  updateSelectedCategories(selectedNodes: TreeNode[]) {
    if (this._onChangeCallback) {
      this._onChangeCallback(selectedNodes.map((n) => n.key as string));
    }
  }

  selectedNodesChanenged(selectedNodes: TreeNode[]) {
    // this.updateSelectedCategories(selectedNodes);
  }

  // Make sure parent nodes are  unselected when one of the child nodes is unselected
  nodeUnselected(event: TreeNodeUnSelectEvent) {
    let currentNode = event.node.parent;
    let root = event.node;

    // Traverse through parents until reaching the root
    while (currentNode) {
      const index = this.selectedNodes.indexOf(currentNode);

      if (index !== -1) {
        // Remove it from selectedNodes
        this.selectedNodes.splice(index, 1);
      }

      if (!currentNode.parent) {
        root = currentNode;
      }
      // Move to the next parent
      currentNode = currentNode.parent;
    }

    this.calculateSelectedProducts(root);

    // Update the form value after removing parent nodes
    this.updateSelectedCategories(this.selectedNodes);
  }

  // Update selectedProducts count for parent nodes when a child node is unselected
  nodeSelected(event: TreeNodeSelectEvent) {
    let root = event.node;
    let currentNode = event.node.parent;

    // Traverse through parents until reaching the root
    while (currentNode) {
      if (!currentNode.parent) {
        root = currentNode;
      }
      // Move to the next parent
      currentNode = currentNode.parent;
    }

    this.calculateSelectedProducts(root);

    // Update the form value after selecting a node
    this.updateSelectedCategories(this.selectedNodes);
  }
}
